import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';
import lang from '../language/Eng_Aust.json';
import { getNextMondayDateString, getWeekDays, formatDate, getMonday } from './utils';
import { buildVisibleOrderItems, removeHiddenOrderItems, removeHiddenQuantities } from './order_utils';

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #FDFBF9;
  padding: 60px 20px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const PillBadge = styled.span`
  background-color: #FFF2D1;
  color: #937A3B;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  display: inline-block;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 48px;
  color: #1A1A1A;
  margin: 0 0 16px 0;
  font-weight: 800;
  letter-spacing: -1px;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: #555;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.5;
`;

const CardsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 30px;
  flex-wrap: wrap;
  max-width: 1200px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: white;
  border-radius: 24px;
  padding: 32px;
  width: 100%;
  max-width: 800px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.03);
  border: 1px solid rgba(0,0,0,0.05);
  display: flex;
  flex-direction: column;
`;

const LocationName = styled.h2`
  color: #1A1A1A;
  margin: 0;
  font-size: 32px;
  font-weight: 700;
  text-align: center;
`;

const WeekNavigation = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 24px;
`;

const NavButton = styled.button`
  background: white;
  border: 1px solid #EAEAEA;
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #333;
  transition: background-color 0.2s;

  &:hover {
    background-color: #F5F5F5;
  }
`;

const WeekLabel = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #1A1A1A;
`;

const DayTabs = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 8px;
  padding-bottom: 12px;
  margin-bottom: 24px;
  
  @media (min-width: 600px) {
    justify-content: center;
  }
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #EAEAEA;
    border-radius: 3px;
  }
`;

const DayTab = styled.button<{ $active: boolean; $shouldHighlight: boolean }>`
  padding: 12px 20px;
  border-radius: 12px;
  border: 1px solid ${props => props.$active ? '#FCE17A' : '#EAEAEA'};
  background-color: ${props => props.$active ? '#FFF2D1' : '#FFF'};
  color: ${props => props.$active ? '#937A3B' : '#666'};
  font-weight: ${props => props.$active ? '700' : '600'};
  font-size: 15px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;

  &:hover {
    background-color: ${props => props.$active ? '#FFF2D1' : '#F5F5F5'};
  }

  ${props => props.$shouldHighlight && !props.$active && `
    border-color: #FFF2D1;
    color: #937A3B;
  `}
`;

const DayNameSpan = styled.span`
  font-size: 14px;
`;

const DayDateSpan = styled.span`
  font-size: 12px;
  opacity: 0.8;
`;

const MenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  margin-bottom: 40px;
`;

const CategorySection = styled.div``;

const CategoryHeader = styled.h3`
  font-size: 22px;
  color: #1A1A1A;
  margin: 0 0 20px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid #F4F4F4;
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ItemInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const ItemName = styled.span`
  font-weight: 500;
  color: #333;
  font-size: 16px;
`;

const QuantityInput = styled.input`
  width: 60px;
  height: 40px;
  border: 1px solid #E5E5E5;
  border-radius: 12px;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  outline: none;
  transition: all 0.2s ease;
  background-color: #FAFAFA;

  &:focus {
    border-color: #F8E287;
    background-color: #FFF;
    box-shadow: 0 0 0 3px rgba(248, 226, 135, 0.2);
  }
`;

const ConfirmButton = styled.button`
  background-color: #FCE17A;
  color: #1A1A1A;
  border: none;
  border-radius: 16px;
  padding: 20px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  margin-top: auto;
  transition: all 0.2s ease;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 14px rgba(252, 225, 122, 0.3);

  &:hover {
    background-color: #F8D654;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(248, 214, 84, 0.4);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(248, 214, 84, 0.4);
  }

  &:disabled {
    background-color: #F0F0F0;
    color: #AAA;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const LogoutButton = styled.button`
  margin-top: 40px;
  background: none;
  border: none;
  color: #999;
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;
  transition: color 0.2s;

  &:hover {
    color: #666;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 24px;
  padding: 32px;
  width: 100%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
`;

const ModalTitle = styled.h2`
  margin: 0 0 20px 0;
  font-size: 24px;
  color: #1A1A1A;
  text-align: center;
`;

const ModalSubtitle = styled.p`
  font-size: 16px;
  color: #666;
  margin: -12px 0 20px 0;
  text-align: center;
`;

const ModalButton = styled.button<{ variant: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 14px 20px;
  font-size: 15px;
  font-weight: 700;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: ${props => props.variant === 'secondary' ? '1px solid #EAEAEA' : 'none'};
  background-color: ${props => props.variant === 'primary' ? '#FCE17A' : '#FFF'};
  color: ${props => props.variant === 'primary' ? '#1A1A1A' : '#333'};

  &:hover {
    background-color: ${props => props.variant === 'primary' ? '#F8D654' : '#F5F5F5'};
  }
`;

const ActionContainer = styled.div`
  margin-top: 32px;
  display: flex;
`;

const SubmitButton = styled.button`
  flex: 1;
  background-color: #FCE17A;
  color: #1A1A1A;
  border: none;
  border-radius: 12px;
  padding: 16px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #F8D654;
  }
  
  &:disabled {
    background-color: #F0F0F0;
    color: #AAA;
    cursor: not-allowed;
  }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #FCE17A;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  animation: ${spin} 1s linear infinite;
  margin-bottom: 16px;
`;

const MENU_CATEGORIES = lang.deliveryMenu;

export interface OrderItem {
  id: string;
  name: string;
  quantity: number;
}

export interface WeeklyOrder {
  id?: string;
  weekStartDate: string;
  createdAt?: any;
  branchId: string;
  branchName: string;
  userEmail: string | null;
  dailyItems: Record<string, OrderItem[]>;
}

const DeliveryStart: React.FC = () => {
  const navigate = useNavigate();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);

  const getCurrentBranch = useCallback(() => {
    let branchName = 'Unknown Branch';
    let branchId = 'unknown';

    if (currentUserEmail?.toLowerCase() === 'hornsbymanager@firestone-sydney.com.au') {
      branchName = 'Firestone Hornsby';
      branchId = 'firestone-hornsby';
    } else if (currentUserEmail?.toLowerCase() === 'chatswood@vuzahospitality.com.au') {
      branchName = 'Chatswood';
      branchId = 'chatswood';
    } else if (currentUserEmail?.toLowerCase() === 'firestoneeastwood@firestone-sydney.com.au') {
      branchName = 'Firestone Eastwood';
      branchId = 'firestone-eastwood';
    }

    return {
      id: branchId,
      name: branchName,
      categories: MENU_CATEGORIES
    };
  }, [currentUserEmail]);

  const activeBranch = getCurrentBranch();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/delivery/login');
      } else {
        setCurrentUserEmail(user.email);
        setIsCheckingAuth(false);
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const [selectedWeek, setSelectedWeek] = useState<string>(getNextMondayDateString());
  const [selectedDayName, setSelectedDayName] = useState<string>('Monday');
  const [sessionOrderConfirmed, setSessionOrderConfirmed] = useState(false);
  const [interactedDays, setInteractedDays] = useState<string[]>(['Monday']);
  
  useEffect(() => {
    setInteractedDays(['Monday']);
    setSelectedDayName('Monday');
    setSessionOrderConfirmed(false);
  }, [selectedWeek]);

  const handleDaySelect = (dayName: string) => {
    setSelectedDayName(dayName);
    if (!interactedDays.includes(dayName)) {
      setInteractedDays(prev => [...prev, dayName]);
    }
  };
  
  const [quantities, setQuantities] = useState<Record<string, Record<string, number>>>({});
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [hiddenItems, setHiddenItems] = useState<string[]>([]);
  const [isMenuEditModalOpen, setIsMenuEditModalOpen] = useState(false);
  const [tempHiddenItems, setTempHiddenItems] = useState<string[]>([]);
  const [isSavingMenuSettings, setIsSavingMenuSettings] = useState(false);

  const handleSaveTemplate = async () => {
    const branch = activeBranch;
    if (!branch) return;
    
    setIsSavingTemplate(true);
    try {
      await setDoc(doc(db, 'branch_quantities', branch.name), {
        branchId: branch.id,
        branchName: branch.name,
        quantities: removeHiddenQuantities(quantities, hiddenItems),
        updatedAt: serverTimestamp(),
        userEmail: currentUserEmail
      });
      setIsTemplateSuccessModalOpen(true);
    } catch (e) {
      console.error(e);
      alert('Failed to save menu quantities.');
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const handleSaveMenuSettings = async () => {
    const branch = activeBranch;
    if (!branch) return;
    
    setIsSavingMenuSettings(true);
    try {
      const orderRef = doc(db, 'weekly_orders', selectedWeek, 'branches', branch.name);
      const templateRef = doc(db, 'branch_quantities', branch.name);
      const [orderSnap, templateSnap] = await Promise.all([
        getDoc(orderRef),
        getDoc(templateRef),
      ]);
      const writes = [
        setDoc(doc(db, 'branch_settings', branch.name), {
          hiddenItems: tempHiddenItems,
          updatedAt: serverTimestamp(),
          userEmail: currentUserEmail
        }, { merge: true }),
      ];

      if (orderSnap.exists()) {
        const savedOrder = orderSnap.data() as WeeklyOrder;
        writes.push(setDoc(orderRef, {
          dailyItems: removeHiddenOrderItems(savedOrder.dailyItems || {}, tempHiddenItems),
        }, { merge: true }));
      }

      if (templateSnap.exists()) {
        const savedTemplate = templateSnap.data();
        writes.push(setDoc(templateRef, {
          quantities: removeHiddenQuantities(savedTemplate.quantities || {}, tempHiddenItems),
        }, { merge: true }));
      }

      await Promise.all(writes);
      setQuantities(prev => removeHiddenQuantities(prev, tempHiddenItems));
      setHiddenItems(tempHiddenItems);
      setIsMenuEditModalOpen(false);
      setIsSettingsSuccessModalOpen(true);
    } catch (e) {
      console.error(e);
      alert('Failed to save menu settings.');
    } finally {
      setIsSavingMenuSettings(false);
    }
  };

  const weekDays = getWeekDays(selectedWeek);

  const handlePrevWeek = () => {
    const [y, m, d] = selectedWeek.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    date.setDate(date.getDate() - 7);
    setSelectedWeek(formatDate(date));
  };

  const handleNextWeek = () => {
    const [y, m, d] = selectedWeek.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    date.setDate(date.getDate() + 7);
    setSelectedWeek(formatDate(date));
  };

  useEffect(() => {
    if (isCheckingAuth || !currentUserEmail) return;

    const fetchWeeklyOrder = async () => {
      setIsLoadingData(true);
      try {
        const branch = getCurrentBranch();
        
        if (currentUserEmail.toLowerCase() === 'butkitchen@butteredsydney.com.au' || !branch.id) {
          setIsLoadingData(false);
          return;
        }

        const docRef = doc(db, 'weekly_orders', selectedWeek, 'branches', branch.name);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const orderData = docSnap.data() as WeeklyOrder;
          const loadedQtys: Record<string, Record<string, number>> = {};
          
          if (orderData.dailyItems) {
            Object.entries(orderData.dailyItems).forEach(([dayName, items]) => {
              loadedQtys[dayName] = {};
              items.forEach(item => {
                loadedQtys[dayName][item.id] = item.quantity;
              });
            });
          }
          setQuantities(loadedQtys);
        } else {
          const templateRef = doc(db, 'branch_quantities', branch.name);
          const templateSnap = await getDoc(templateRef);
          if (templateSnap.exists()) {
            const templateData = templateSnap.data();
            setQuantities(templateData.quantities || {});
          } else {
            setQuantities({});
          }
        }

        const settingsRef = doc(db, 'branch_settings', branch.name);
        const settingsSnap = await getDoc(settingsRef);
        if (settingsSnap.exists()) {
          setHiddenItems(settingsSnap.data().hiddenItems || []);
        } else {
          setHiddenItems([]);
        }
      } catch (error) {
        console.error('Error fetching weekly order:', error);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchWeeklyOrder();
  }, [isCheckingAuth, currentUserEmail, selectedWeek, getCurrentBranch]);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isTemplateSuccessModalOpen, setIsTemplateSuccessModalOpen] = useState(false);
  const [isSettingsSuccessModalOpen, setIsSettingsSuccessModalOpen] = useState(false);
  const [modalItemsByDay, setModalItemsByDay] = useState<Record<string, OrderItem[]>>({});

  const handleQuantityChange = (dayName: string, itemId: string, value: string) => {
    const val = parseInt(value, 10);
    const newQty = isNaN(val) ? 0 : Math.max(0, val);
    
    setQuantities(prev => ({
      ...prev,
      [dayName]: {
        ...(prev[dayName] || {}),
        [itemId]: newQty
      }
    }));
  };

  const getTotalQuantity = () => {
    let sum = 0;
    Object.values(removeHiddenQuantities(quantities, hiddenItems)).forEach(dayQtys => {
      Object.values(dayQtys).forEach(qty => {
        sum += qty;
      });
    });
    return sum;
  };

  const hasOrdersForDay = (dayName: string) => {
    const dayQtys = removeHiddenQuantities(quantities, hiddenItems)[dayName] || {};
    return Object.values(dayQtys).some(qty => qty > 0);
  };

  const handleOpenModal = () => {
    const branch = activeBranch;
    if (!branch) return;

    const itemsByDay = buildVisibleOrderItems(
      branch.categories,
      quantities,
      hiddenItems,
      ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    );

    if (Object.keys(itemsByDay).length > 0) {
      setModalItemsByDay(itemsByDay);
      setIsModalOpen(true);
    }
  };

  const handleConfirmOrder = async () => {
    if (isSubmitting) return;
    
    const branch = activeBranch;
    if (!branch) return;

    setIsSubmitting(true);

    const newOrder: WeeklyOrder = {
      weekStartDate: selectedWeek,
      createdAt: serverTimestamp(),
      branchId: branch.id,
      branchName: branch.name,
      userEmail: currentUserEmail,
      dailyItems: removeHiddenOrderItems(modalItemsByDay, hiddenItems),
    };

    try {
      await setDoc(doc(db, 'weekly_orders', selectedWeek, 'branches', branch.name), newOrder);
      setSessionOrderConfirmed(true);
      setIsModalOpen(false);
      setIsSuccessModalOpen(true);
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Error saving order:', error);
      alert(lang.start.saveError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/delivery/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  if (isCheckingAuth || isLoadingData) {
    return (
      <PageContainer style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <Spinner />
        <h2 style={{ color: '#666', fontSize: '18px', fontWeight: 500 }}>{lang.start.loading}</h2>
      </PageContainer>
    );
  }

  const isButtonDisabled = getTotalQuantity() === 0;

  return (
    <PageContainer>
      <HeaderSection>
        <PillBadge>{lang.start.bakeryFreshness}</PillBadge>
        <Title>{lang.start.placeOrder}</Title>
        <Subtitle>
          Select a week, pick a day, and enter your order quantities. 
          You can order for the whole week at once.
        </Subtitle>
      </HeaderSection>

      <CardsContainer>
        <Card>
          <div style={{ paddingBottom: '24px', borderBottom: '1px solid #EAEAEA', marginBottom: '32px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
              <LocationName>{activeBranch.name}</LocationName>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button 
                  onClick={handleSaveTemplate}
                  disabled={isSavingTemplate}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#FFF',
                    color: '#1A1A1A',
                    border: '1px solid #EAEAEA',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: isSavingTemplate ? 'not-allowed' : 'pointer',
                    opacity: isSavingTemplate ? 0.7 : 1,
                  }}>
                  {isSavingTemplate ? 'Saving...' : 'Save Menu Quantities'}
                </button>
                <button 
                  onClick={() => { setTempHiddenItems(hiddenItems); setIsMenuEditModalOpen(true); }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#FFF',
                    color: '#1A1A1A',
                    border: '1px solid #EAEAEA',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}>
                  Edit Menu
                </button>
              </div>
            </div>
            
            <WeekNavigation style={{ marginTop: '24px' }}>
              <NavButton onClick={handlePrevWeek}>
                <ChevronLeft size={20} />
              </NavButton>
              <WeekLabel>
                Week of {weekDays[0].shortDate} - {weekDays[6].shortDate}
              </WeekLabel>
              <NavButton onClick={handleNextWeek}>
                <ChevronRight size={20} />
              </NavButton>
            </WeekNavigation>

            <DayTabs>
              {weekDays.map(day => (
                <DayTab 
                  key={day.dayName} 
                  $active={selectedDayName === day.dayName}
                  $shouldHighlight={hasOrdersForDay(day.dayName) && (sessionOrderConfirmed || interactedDays.includes(day.dayName))}
                  onClick={() => handleDaySelect(day.dayName)}
                >
                  <DayNameSpan>{day.shortName}</DayNameSpan>
                  <DayDateSpan>{day.shortDate}</DayDateSpan>
                </DayTab>
              ))}
            </DayTabs>
          </div>

          <MenuContainer>
            {activeBranch.categories.map((category, catIdx) => {
              const visibleItems = category.items.filter(item => !hiddenItems.includes(item.id));
              if (visibleItems.length === 0) return null;
              return (
                <CategorySection key={catIdx}>
                  <CategoryHeader>{category.category}</CategoryHeader>
                  {visibleItems.map(item => (
                    <ItemRow key={item.id}>
                      <ItemInfo>
                        <ItemName>{item.name}</ItemName>
                      </ItemInfo>
                      <QuantityInput 
                        type="number" 
                        min="0"
                        value={quantities[selectedDayName]?.[item.id] || ''}
                        onChange={(e) => handleQuantityChange(selectedDayName, item.id, e.target.value)}
                        placeholder="0"
                      />
                    </ItemRow>
                  ))}
                </CategorySection>
              );
            })}
          </MenuContainer>

          <ActionContainer>
            <SubmitButton disabled={isButtonDisabled} onClick={handleOpenModal}>
              {lang.start.submitOrder} (Weekly Total: {getTotalQuantity()})
            </SubmitButton>
          </ActionContainer>
        </Card>
      </CardsContainer>

      {isModalOpen && (
        <ModalOverlay onClick={() => setIsModalOpen(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalTitle>Weekly Order Summary</ModalTitle>
            <ModalSubtitle>{activeBranch.name} | {weekDays[0].shortDate} - {weekDays[6].shortDate}</ModalSubtitle>
            
            <div style={{ marginBottom: '24px' }}>
              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => {
                const items = modalItemsByDay[day];
                if (!items || items.length === 0) return null;
                
                return (
                  <div key={day} style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#937A3B', borderBottom: '1px solid #FFF2D1', paddingBottom: '4px', marginBottom: '8px' }}>
                      {day}
                    </div>
                    {items.map(item => (
                      <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '15px' }}>
                        <span style={{ color: '#333' }}>{item.name}</span>
                        <strong style={{ color: '#1A1A1A' }}>{item.quantity}x</strong>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <ModalButton variant="secondary" onClick={() => setIsModalOpen(false)}>
                Cancel
              </ModalButton>
              <ModalButton variant="primary" onClick={handleConfirmOrder} disabled={isSubmitting} style={{ opacity: isSubmitting ? 0.7 : 1 }}>
                {isSubmitting ? 'Saving...' : 'Confirm Weekly Order'}
              </ModalButton>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}

      {isSuccessModalOpen && (
        <ModalOverlay onClick={() => setIsSuccessModalOpen(false)}>
          <ModalContent style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
            <div style={{ marginBottom: '16px' }}>
              <CheckCircle2 size={48} color="#22C55E" />
            </div>
            <ModalTitle>{lang.start.successTitle}</ModalTitle>
            <ModalSubtitle>Your weekly order has been successfully saved.</ModalSubtitle>
            
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
              <ModalButton variant="primary" onClick={() => setIsSuccessModalOpen(false)}>
                {lang.start.close}
              </ModalButton>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}

      {isTemplateSuccessModalOpen && (
        <ModalOverlay onClick={() => setIsTemplateSuccessModalOpen(false)}>
          <ModalContent style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
            <div style={{ marginBottom: '16px' }}>
              <CheckCircle2 size={48} color="#22C55E" />
            </div>
            <ModalTitle>Template Saved!</ModalTitle>
            <ModalSubtitle>Menu quantities have been saved as default.</ModalSubtitle>
            
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
              <ModalButton variant="primary" onClick={() => setIsTemplateSuccessModalOpen(false)}>
                {lang.start.close}
              </ModalButton>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}

      {isSettingsSuccessModalOpen && (
        <ModalOverlay onClick={() => setIsSettingsSuccessModalOpen(false)}>
          <ModalContent style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
            <div style={{ marginBottom: '16px' }}>
              <CheckCircle2 size={48} color="#22C55E" />
            </div>
            <ModalTitle>Settings Saved!</ModalTitle>
            <ModalSubtitle>Your menu visibility settings have been saved.</ModalSubtitle>
            
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
              <ModalButton variant="primary" onClick={() => setIsSettingsSuccessModalOpen(false)}>
                {lang.start.close}
              </ModalButton>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}

      <div style={{ textAlign: 'center', width: '100%', marginTop: '40px' }}>
        <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
      </div>

      {isMenuEditModalOpen && (
        <ModalOverlay onClick={() => setIsMenuEditModalOpen(false)}>
          <ModalContent onClick={e => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '80vh', overflowY: 'auto' }}>
            <ModalTitle>Edit Menu Visibility</ModalTitle>
            <ModalSubtitle>Uncheck items you don't need for {activeBranch.name}</ModalSubtitle>
            
            <div style={{ marginTop: '24px', textAlign: 'left' }}>
              {activeBranch.categories.map((category, catIdx) => (
                <div key={catIdx} style={{ marginBottom: '24px' }}>
                  <div style={{ fontSize: '16px', fontWeight: 600, color: '#1A1A1A', borderBottom: '2px solid #FFF2D1', paddingBottom: '8px', marginBottom: '12px' }}>
                    {category.category}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                    {category.items.map(item => {
                      const isHidden = tempHiddenItems.includes(item.id);
                      return (
                        <label key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: isHidden ? '#999' : '#333' }}>
                          <input 
                            type="checkbox" 
                            checked={!isHidden} 
                            onChange={(e) => {
                              if (e.target.checked) {
                                setTempHiddenItems(prev => prev.filter(id => id !== item.id));
                              } else {
                                setTempHiddenItems(prev => [...prev, item.id]);
                              }
                            }}
                            style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#FCE17A' }}
                          />
                          {item.name}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '24px' }}>
              <ModalButton variant="secondary" onClick={() => setIsMenuEditModalOpen(false)}>
                Cancel
              </ModalButton>
              <ModalButton variant="primary" onClick={handleSaveMenuSettings} disabled={isSavingMenuSettings} style={{ opacity: isSavingMenuSettings ? 0.7 : 1 }}>
                {isSavingMenuSettings ? 'Saving...' : 'Save Settings'}
              </ModalButton>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default DeliveryStart;
